// Details the issue report panel attaches automatically, so a reporter never has to type
// the page, browser or what just went wrong in the console.

declare const __APP_VERSION__: string;

const MAX_ERRORS = 20;
const MAX_LEN = 500;
const recentErrors: string[] = [];
let installed = false;

function remember(msg: string) {
  const line = `${new Date().toISOString().slice(11, 19)} ${msg}`.slice(0, MAX_LEN);
  recentErrors.push(line);
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
}

function stringify(v: unknown): string {
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

/** Keeps the last 20 console errors / uncaught errors in memory. Call once at startup. */
export function installConsoleErrorBuffer() {
  if (installed) return;
  installed = true;
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try { remember(args.map(stringify).join(" ")); } catch { /* never break logging */ }
    original(...args);
  };
  window.addEventListener("error", (e) => remember(`Uncaught ${stringify(e.error ?? e.message)}`));
  window.addEventListener("unhandledrejection", (e) => remember(`Unhandled rejection ${stringify(e.reason)}`));
}

// Order matters: Edge and Opera also say "Chrome", and Chrome also says "Safari".
const BROWSERS: [name: string, pattern: RegExp][] = [
  ["Edge", /Edg\/(\d+)/],
  ["Opera", /OPR\/(\d+)/],
  ["Firefox", /Firefox\/(\d+)/],
  ["Chrome", /Chrome\/(\d+)/],
  ["Safari", /Version\/(\d+).*Safari/],
];

export function detectBrowser(ua: string): string {
  for (const [name, pattern] of BROWSERS) {
    const m = ua.match(pattern);
    if (m) return `${name} ${m[1]}`;
  }
  return "Unknown browser";
}

export function detectOs(ua: string): string {
  if (/Windows NT/.test(ua)) return "Windows";
  if (/Android/.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Linux/.test(ua)) return "Linux";
  return "Unknown OS";
}

export interface IssueContext {
  pageUrl: string;
  routePath: string;
  userAgent: string;
  browser: string;
  os: string;
  viewport: string;
  appVersion: string;
  consoleErrors: string[];
}

export function collectIssueContext(): IssueContext {
  const ua = navigator.userAgent;
  return {
    pageUrl: window.location.href.slice(0, 2048),
    routePath: window.location.pathname.slice(0, 512),
    userAgent: ua.slice(0, 512),
    browser: detectBrowser(ua),
    os: detectOs(ua),
    viewport: `${window.innerWidth}×${window.innerHeight} @${window.devicePixelRatio || 1}x`,
    appVersion: typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "unknown",
    consoleErrors: [...recentErrors],
  };
}
